import { Injectable } from '@angular/core';
import { forOwn, isEmpty, map, reduce } from 'lodash';
import { BrowserManagerDataService } from '../browser-manager-data/browser-manager-data.service';
import { WindowRefService } from 'i2g-ng-utils';

export const DEFAULT_POLLING_INTERVAL = 1000 * 60 * 10; // 10 minutes

export interface IPollers {
  [intervalId: number]: IPoller;
}

export interface IPoller {
  fn: (...args: any[]) => void;
  interval: number;
  unregistered: boolean;
}

@Injectable()
export class PollerService {
  private _pollers: IPollers = {};
  private _windowRef: any;

  constructor(
    windowRefService: WindowRefService,
    browserManagerDataService: BrowserManagerDataService
  ) {
    this._windowRef = windowRefService.nativeWindow;

    browserManagerDataService.tabVisible$.subscribe((tabVisible: boolean) => {
      if (!tabVisible) {
        this.pauseAllPollings();
      } else {
        if (!isEmpty(this._pollers)) {
          this.restartPollings();
        }
      }
    });
  }

  registerPolling(
    fn: (...args: any[]) => void,
    interval: number = DEFAULT_POLLING_INTERVAL
  ): number {
    const intervalId = this._windowRef.setInterval(fn, interval);

    this._pollers[intervalId] = {
      fn,
      interval,
      unregistered: false,
    };

    return intervalId;
  }

  unregisterPollingById(id: number) {
    clearInterval(id);

    this._pollers[id].unregistered = true;
  }

  unregisterPollingByFn(fn: (...args: any[]) => void) {
    forOwn(this._pollers, (poller: IPoller, intervalId: string) => {
      if (poller.fn === fn) {
        this.unregisterPollingById(parseInt(intervalId, 10));
      }
    });
  }

  pauseAllPollings() {
    forOwn(this._pollers, (_, intervalId: any) =>
      clearInterval(parseInt(intervalId, 10))
    );
  }

  restartPollings() {
    map(this._pollers, (poller: IPoller) => {
      if (!poller.unregistered) {
        poller.fn();
      }
    });

    this._pollers = reduce(
      this._pollers,
      (result: any, poller: IPoller) => {
        if (!poller.unregistered) {
          const pollerFn = poller.fn;
          const pollerInterval = poller.interval;
          const intervalId = this._windowRef.setInterval(
            pollerFn,
            pollerInterval
          );

          result[intervalId] = {
            fn: pollerFn,
            interval: pollerInterval,
          };
        }

        return result;
      },
      {}
    );
  }
}
